// src/components/Typewriter.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

export default function Typewriter({
  text,
  speed = 100,          // ms per character
  delay = 500,          // initial delay before typing starts
  cursor = '|',         // the cursor character
  cursorBlinkSpeed = 500,
  loopDelay = 10000     // ms to wait after finishing before restarting
}) {
  const [displayed, setDisplayed] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  const idxRef           = useRef(0);
  const typingTimerRef   = useRef(null);
  const startTimerRef    = useRef(null);
  const blinkTimerRef    = useRef(null);
  const restartTimerRef  = useRef(null);

  useEffect(() => {
    // reset state
    setDisplayed('');
    setShowCursor(true);
    idxRef.current = 0;

    // start typing
    startTimerRef.current = setTimeout(() => {
      const typeNext = () => {
        if (idxRef.current < text.length) {
          const nextChar = text.charAt(idxRef.current);
          setDisplayed((d) => d + nextChar);
          idxRef.current += 1;
          typingTimerRef.current = setTimeout(typeNext, speed);
        } else {
          // finished typing → schedule a restart after loopDelay
          restartTimerRef.current = setTimeout(() => {
            setDisplayed('');
            idxRef.current = 0;
            // small delay before retyping
            startTimerRef.current = setTimeout(typeNext, delay);
          }, loopDelay);
        }
      };
      typeNext();
    }, delay);

    // blink cursor continuously
    blinkTimerRef.current = setInterval(() => {
      setShowCursor((c) => !c);
    }, cursorBlinkSpeed);

    return () => {
      // clear all timers on unmount or when dependencies change
      clearTimeout(startTimerRef.current);
      clearTimeout(typingTimerRef.current);
      clearInterval(blinkTimerRef.current);
      clearTimeout(restartTimerRef.current);
    };
  }, [text, speed, delay, cursorBlinkSpeed, loopDelay]);

  return (
    <span className="typewriter" style={{ whiteSpace: 'pre' }}>
      {displayed}
      <span className="cursor">{showCursor ? cursor : ''}</span>
    </span>
  );
}

Typewriter.propTypes = {
  text:             PropTypes.string.isRequired,
  speed:            PropTypes.number,
  delay:            PropTypes.number,
  cursor:           PropTypes.string,
  cursorBlinkSpeed: PropTypes.number,
  loopDelay:        PropTypes.number,
};
